import { Alert, Checkbox, LinkButton } from '@pmi/dsm-react';
import { CircleInfoIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { AlertWarningIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { WizardAction, WizardStep } from '.';
import ProductDetailModal from '../../../Admin/Dashboard/DealTabs/DealProductsTable/ProductDetailModal';
import { useRestoreUserMutation } from '../../../Admin/People/TabsCard/UsersTab/DisableUserModal/apiSlice';
import { mapPaymentArrangementText } from '../../constants';
import {
  getResponseErrorMessage,
  getUserText,
  toDayMonthYear,
} from '../../helpers';
import useUser from '../../hooks/useUser';
import { AgreementApiModel } from '../../models/DealModel';
import { ProductApiModel } from '../../models/ProductModel';
import { User } from '../../models/UserModels';
import { accountSelector } from '../../store/shared/accountSlice';
import checkExpiryStatus from '../../utils/checkExpiryStatus';
import getNetQuantity from '../../utils/getNetQuantity';
import isProductsDuplicates from '../../utils/isProductDuplicates';
import printableZoneRestrictionLabel from '../../utils/printableZoneRestrictionLabel';
import CategoryLabel from '../CategoryLabel';
import RenderHtml from '../RenderHtml';
import styles from './agreementCard.module.scss';
import {
  useCreateAssignmentMutation,
  useEnableUserBulkMutation,
  useRemoveAssignmentMutation,
  useRestoreAssignmentsMutation,
} from './apiSlice';
import AssignProductsConfirmModal from './AssignProductsConfirmModal';
import ProductQuantityInfo, {
  getLowQuantityThreshold,
} from './ProductQuantityInfo';
import AgreementSelectedProducts from './AgreementSelectedProducts';
import usePermissions from '../../hooks/usePermissions';
import { toastWithAnalytics } from '../../analytics';
import {
  dealHasExpired,
  dealHasNoAvailableProducts,
} from '../../utils/dataHelpers';

interface AgreementProductSelectorProps {
  agreement: AgreementApiModel;
  setStep?: Dispatch<SetStateAction<WizardStep>> | null;
  setProducts?: Dispatch<SetStateAction<ProductApiModel[]>> | null;
  defaultProducts: ProductApiModel[];
  people?: User[];
  action?: WizardAction;
  headerText?: string;
  disabledProductIds?: number[];
  hasBackButton?: boolean;
  onSuccess?: (() => void) | undefined;
}

export default function AgreementProductSelector({
  agreement,
  setStep = null,
  setProducts = null,
  defaultProducts,
  people = undefined,
  action = 'add',
  headerText = 'Select one or more products',
  disabledProductIds = [],
  hasBackButton = false,
  onSuccess = undefined,
}: AgreementProductSelectorProps) {
  const user = useUser();
  const { currentAccountId } = useSelector(accountSelector);
  const { restrictedAgreementIds } = usePermissions();
  const isUnassignmentRestricted =
    action === 'remove' && restrictedAgreementIds.includes(agreement.id);
  const [selectedProducts, setSelectedProducts] =
    useState<ProductApiModel[]>(defaultProducts);
  const [createAssignment] = useCreateAssignmentMutation();
  const [removeAssignment] = useRemoveAssignmentMutation();
  const [restoreAssignment] = useRestoreAssignmentsMutation();
  const [enableUsers] = useEnableUserBulkMutation();
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<
    ProductApiModel | undefined
  >(undefined);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [duplicatedProductName, setDuplicatedProductName] = useState<
    string | undefined
  >();
  const [submitting, setSubmitting] = useState(false);
  const onModalCancel = () => setShowProductModal(false);
  const onMoreDetailsClick = (product: ProductApiModel) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  const [restoreUser] = useRestoreUserMutation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const onClick = (remove: boolean, product: ProductApiModel) => {
    const find = selectedProducts.filter((p) => p.sku === product.sku);
    if (find && !remove) {
      setSelectedProducts([...selectedProducts, product]);
      return;
    }
    if (remove) {
      setSelectedProducts(
        selectedProducts.filter((p) => p.sku !== product.sku),
      );
    }
  };
  const onClickAddPeople = () => {
    if (setProducts) {
      setProducts(selectedProducts);
    }
    if (setStep) {
      setStep('user-email');
    }
  };

  const submitProductsAction = async () => {
    if (!people || people?.length === 0 || selectedProducts.length === 0) {
      return;
    }
    const userIds = people.map((p) => Number(p.id));
    const productIds = selectedProducts.map((p) => p.accountProductId);
    setSubmitting(true);
    if (action === 'add') {
      let msg =
        userIds.length === 0
          ? `You have added ${selectedProducts.length} products to ${people?.length} people from agreement: ${agreement.id}`
          : `You have successfully added ${
              productIds.length > 1 ? '' : 'a '
            }product${productIds.length > 1 ? 's' : ''}`;
      let errorMsg = `Error! Unable to add ${selectedProducts.length} product${
        productIds.length > 1 ? 's' : ''
      } from agreement: ${agreement.id}`;
      let requestToDo = null;
      if (userIds.length === 1) {
        if (people[0].isRemoved) {
          requestToDo = restoreUser({
            accountId: currentAccountId,
            userId: userIds[0].toString(),
            productIds,
          });
          msg = `You have enabled ${getUserText(people[0])} and assigned ${
            productIds.length > 1 ? '' : 'a '
          }product${productIds.length > 1 ? 's' : ''}`;
          errorMsg = `Error! Unable to enable user and failed to add ${
            selectedProducts.length
          } product${productIds.length > 1 ? 's' : ''} from
              agreement: ${agreement.id}`;
        }
      }
      if (!requestToDo) {
        requestToDo = createAssignment({
          userIds,
          modifiedByPersonFullName: `${user.given_name} ${user.family_name}`,
          modifiedByPersonId: user.person_id,
          accountProductIds: productIds,
        });
      }
      requestToDo
        .unwrap()
        .then(() => {
          toastWithAnalytics(
            <span className="fw-normal">{msg}</span>,
            'success',
          );
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch(() => {
          toastWithAnalytics(
            <span className="fw-normal">{errorMsg}</span>,
            'danger',
          );
          toastWithAnalytics(
            <span className="fw-normal">
              Error! Unable to remove {selectedProducts.length} products from
              agreement: {agreement.id}
            </span>,
            'danger',
          );
        })
        .finally(() => setSubmitting(false));
    } else if (action === 'remove') {
      const assignmentIds = selectedProducts.flatMap((selectedItem) =>
        people.flatMap((person) =>
          person.assignedProducts
            .filter(
              (assignedProduct) =>
                assignedProduct.name === selectedItem.name &&
                assignedProduct.quantityRedeemed === 0,
            )
            .map((assignedProduct) => assignedProduct.assignmentId),
        ),
      );

      if (assignmentIds.length === 0) {
        toastWithAnalytics(
          <span className="fw-normal">
            Unable to remove products — all selected assignments have been
            redeemed.
          </span>,
          'danger',
        );
        setSubmitting(false);
        return;
      }

      removeAssignment({
        userAssignmentIds: assignmentIds,
        modifiedByPersonFullName: `${user.given_name} ${user.family_name}`,
        modifiedByPersonId: user.person_id,
        agreementId: agreement.id,
      })
        .unwrap()
        .then(() => {
          toastWithAnalytics(
            <span className="fw-normal">
              You have unassigned {selectedProducts.length} product
              {selectedProducts.length > 1 ? 's ' : ' '}
              from{' '}
              {people?.length > 1
                ? `${people.length} people`
                : `${people.length} person`}
              .
            </span>,
            'success',
          );
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch(() => {
          toastWithAnalytics(
            <span className="fw-normal">
              Error! Unable to remove {selectedProducts.length} products from
              agreement:{agreement.id}
            </span>,
            'danger',
          );
        })
        .finally(() => setSubmitting(false));
    } else if (action === 'restore' && people.length === 1) {
      const person = people[0];
      const errorMessage = (err: any): ReactNode => {
        const message = getResponseErrorMessage(err);
        if (message) {
          return (
            <span className="fw-normal">
              {message
                .replace('remove', 'disable')
                .replace('removing', 'disabling')}
            </span>
          );
        }
        return (
          <span className="fw-normal">
            There was an error enabling{' '}
            <span data-exclude-lg="true">{getUserText(person)}</span>. Please
            try again.
          </span>
        );
      };
      restoreAssignment({
        productAssignments: productIds,
        accountUserId: Number(people[0].id),
        accountId: currentAccountId,
      })
        .unwrap()
        .then(() => {
          toastWithAnalytics(
            <span data-exclude-lg="true">
              You have enabled {getUserText(person)}.
            </span>,
            'success',
          );
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch((err) => {
          toastWithAnalytics(errorMessage(err), 'danger');
        })
        .finally(() => setSubmitting(false));
    } else if (action === 'restoreBulk' && people.length > 1) {
      const restoreUsers = people.map((p) => ({
        userId: p.id,
        productAssignments: productIds,
      }));
      enableUsers({
        accountId: currentAccountId,
        data: { restoreUsers },
      })
        .unwrap()
        .then(() => {
          const message = (): ReactNode => (
            <>
              <span data-exclude-lg="true">{people.length}</span> user
              {people.length > 1 ? 's' : ''}{' '}
              {people.length > 1 ? 'have' : 'has'} been enabled.
            </>
          );

          toastWithAnalytics(message(), 'success');
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch(() => {
          toastWithAnalytics(
            `There was an error enabling user${
              people.length > 1 ? 's' : ''
            } access.`,
            'danger',
          );
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      setSubmitting(false);
    }
  };
  const onClickProductsAction = async () => {
    const duplicatedProduct = isProductsDuplicates(people, selectedProducts);
    if (duplicatedProduct && action === 'add') {
      setConfirmationModalOpen(true);
      setDuplicatedProductName(duplicatedProduct);
      return;
    }

    await submitProductsAction();
  };
  const peopleText =
    action === 'remove' ? 'Remove Products' : 'Assign Products';
  const buttonText = people ? peopleText : 'Add People';
  const products = agreement.products.filter((x) => !x.retired) ?? [];

  // Filter products to show only common products when action is 'remove'
  const displayedProducts =
    action === 'remove' && people && people?.length > 0
      ? products.filter((product) =>
          people.every((person) =>
            person.assignedProducts.some(
              (assignedProduct) => assignedProduct.sku === product.sku,
            ),
          ),
        )
      : products;

  const { isExpiring } = checkExpiryStatus(agreement.expiration);
  const excludedAssignments =
    action === 'remove' && people
      ? selectedProducts.flatMap((selectedItem) =>
          people
            .filter((person) =>
              person.assignedProducts.some(
                (ap) => ap.sku === selectedItem.sku && ap.quantityRedeemed > 0,
              ),
            )
            .map((person) => ({
              personName: person.personName,
              productName: selectedItem.name,
            })),
        )
      : [];
  const isSelected = (product: ProductApiModel) =>
    selectedProducts.find((p) => p.sku === product.sku);

  const zoneTextDisplay = (deal: AgreementApiModel) => {
    const countryLabels: { [key: string]: string } = {
      BR: 'Brazil',
      IN: 'India',
      US: 'the United States',
    };

    // Filter and map zones to their respective country labels
    const countryRestriction =
      deal.zones && deal.zones.length > 0
        ? deal.zones
            .filter((zone) => zone.length > 1)
            .map((zone) => countryLabels[zone])
            .sort()
        : [];
    let finalText = '';
    // Filter zones with a single character
    const zones: string[] = deal.zones.filter((zone) => zone.length === 1);
    let zoneText = '';
    if (zones.length > 2) {
      zoneText = `a Zone ${zones.join('/')} country`;
    }
    if (zones.length <= 2) {
      zoneText = `a Zone ${zones.join(' or ')} country`;
    }
    let countryText = '';
    if (countryRestriction.length > 2) {
      countryText = countryRestriction.join('/');
    }
    if (countryRestriction.length <= 2) {
      countryText = countryRestriction.join(' or ');
    }
    if (countryText.length > 0 || zoneText.length > 0) {
      if (countryRestriction.length > 0) {
        finalText = `${zoneText} or ${countryText}`;
      } else {
        finalText = `${zoneText}`;
      }
    }
    return finalText;
  };

  const isAgreementExpired = dealHasExpired(agreement);
  const isAgreementSoldOut =
    !isAgreementExpired && dealHasNoAvailableProducts(agreement);
  const isAgreementDisabled =
    isAgreementExpired || isAgreementSoldOut || !agreement.enabled;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.querySelector('.modal-body')?.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.agreementProductSelector}>
      <div className="d-block">
        <h3 className={!hasBackButton ? 'mt-4' : undefined}>{headerText}</h3>
        {action === 'remove' && (
          <p className="mt-2">
            Displaying products that all of the selected people share in common
          </p>
        )}
        {(agreement.zones.length > 0 &&
          mapPaymentArrangementText(agreement) === 'Pay by User') ||
          (!!people && (
            <Alert
              variant="info"
              icon={CircleInfoIcon}
              alertText={`These products are included in the selected ${mapPaymentArrangementText(
                agreement,
              )} agreement and can be assigned to individuals in a ${printableZoneRestrictionLabel(
                agreement,
              )}.`}
            />
          ))}
        {agreement.zones.length > 0 &&
          mapPaymentArrangementText(agreement) !== 'Pay by User' &&
          !people && (
            <Alert
              variant="neutral"
              alertText={`These products are compatible with people that live in ${zoneTextDisplay(
                agreement,
              )}.`}
            />
          )}
        {isExpiring && (
          <Alert
            variant="warning"
            icon={AlertWarningIcon}
            alertText={`This agreement is set to expire on ${toDayMonthYear(
              agreement.expiration,
            )}. These products must be enrolled in prior to this date, otherwise they will become inaccessible.`}
          />
        )}
        <div />
      </div>
      <hr className="mt-3 mb-4" />
      <div className={`${styles.agreementProductSelectorBlocks} gap-5`}>
        <div className="d-block flex-grow-1">
          <div className="d-flex gap-4 mb-2">
            <LinkButton
              titleText="Select All Products"
              size="sm"
              variant="light"
              disabled={isAgreementDisabled}
              className={`${styles.btnSelectAllProducts} p-0`}
              onClick={() => {
                const availableProducts = displayedProducts.filter(
                  (p) =>
                    !disabledProductIds.includes(p.accountProductId) &&
                    (agreement.isPayByUser ||
                      (people
                        ? p.quantityRemaining >= people?.length
                        : p.quantityRemaining > 0)),
                );
                setSelectedProducts(availableProducts);
              }}
            />
            {selectedProducts.length >= 2 && (
              <LinkButton
                titleText="Clear Selected"
                size="sm"
                variant="light"
                className={`${styles.btnSelectAllProducts} p-0`}
                onClick={() => {
                  setSelectedProducts([]);
                }}
              />
            )}
          </div>
          <ul className={styles.productCardUl}>
            {displayedProducts
              .filter((p) => !disabledProductIds.includes(p.accountProductId))
              .map((p) => {
                const threshold = getLowQuantityThreshold(getNetQuantity(p));
                const isLowQuantity =
                  p.quantityRemaining / getNetQuantity(p) <= threshold;
                const disabled =
                  isAgreementDisabled ||
                  (action !== 'remove' &&
                    !agreement.isPayByUser &&
                    (p.quantityRemaining < people?.length ||
                      p.quantityRemaining <= 0));

                let quantityRemaining = agreement.isPayByUser
                  ? '∞'
                  : p.quantityRemaining;
                if (isAgreementDisabled) {
                  quantityRemaining = 0;
                }
                return (
                  <li
                    className={`${styles.productBlockCard} ${
                      p.quantityRemaining <= 0 || isAgreementDisabled
                        ? styles.productBlockCardNoQty
                        : ''
                    } ${
                      isSelected(p) ? styles.productBlockCardActive : ''
                    } mb-3 rounded-1`}
                    key={`prodSel${p.sku}`}
                  >
                    {action !== 'remove' && !agreement.isPayByUser && (
                      <ProductQuantityInfo
                        total={getNetQuantity(p)}
                        available={p.quantityRemaining}
                        peopleLength={people?.length}
                      />
                    )}
                    <div className={`d-flex ${styles.content}`}>
                      <div className="d-block border-end align-items-center align-content-center pe-2">
                        <Checkbox
                          disabled={disabled}
                          onChange={(event, checked) => {
                            onClick(!checked, p);
                          }}
                          checked={disabled ? false : !!isSelected(p)}
                          value={p.sku}
                        />
                      </div>
                      <div className="d-flex flex-column px-3 flex-grow-1">
                        <div className="d-block">
                          <CategoryLabel type={p.type} className="mb-2" />
                        </div>
                        <div className="d-block">
                          <RenderHtml
                            html={p?.name && p.name.length > 0 ? p.name : p.sku}
                            tag="b"
                          />
                        </div>
                        <div
                          className={`d-block ${styles.productDescriptionEllipsis}`}
                        >
                          <RenderHtml
                            html={`${
                              p?.shortDescription &&
                              p.shortDescription.length > 0
                                ? p?.shortDescription?.substring(0, 150)
                                : (p?.fullDescription ?? '').substring(0, 150)
                            }...`}
                            tag="p"
                            className="small text-muted text-wrap"
                          />
                          <div className="d-flex pt-2 w-100 flex-sm-row flex-column align-items-start align-items-sm-center">
                            <div className="d-flex gap-1 gap-sm-3 small flex-grow-1 flex-sm-row flex-column">
                              <span
                                className={
                                  isLowQuantity && !agreement.isPayByUser
                                    ? 'text-danger'
                                    : ''
                                }
                              >
                                Available <strong>{quantityRemaining}</strong>
                              </span>
                              <span>
                                Assigned{' '}
                                <strong>
                                  {isAgreementDisabled ? 0 : p.quantityAssigned}
                                </strong>
                              </span>
                              {!agreement.isPayByUser && (
                                <span>
                                  Total{' '}
                                  <strong>
                                    {isAgreementDisabled
                                      ? 0
                                      : getNetQuantity(p)}
                                  </strong>
                                </span>
                              )}
                            </div>
                            <div className="d-block">
                              <LinkButton
                                titleText="More Details"
                                size="xs"
                                variant="light"
                                className={`${styles.prodMoreDetailsLink} p-0`}
                                onClick={() => onMoreDetailsClick(p)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
        <AgreementSelectedProducts
          excludedAssignments={excludedAssignments}
          products={selectedProducts}
          deal={agreement}
          onClick={
            people?.length > 0 ? onClickProductsAction : onClickAddPeople
          }
          disabled={
            displayedProducts.length === 0 ||
            submitting ||
            isUnassignmentRestricted
          }
          buttonText={buttonText}
          people={people}
        />
      </div>
      {confirmationModalOpen && duplicatedProductName && (
        <AssignProductsConfirmModal
          onClose={() => setConfirmationModalOpen(false)}
          onContinue={submitProductsAction}
          productName={duplicatedProductName}
        />
      )}
      {showProductModal && selectedProduct && (
        <ProductDetailModal
          onCancel={onModalCancel}
          product={selectedProduct}
        />
      )}
    </div>
  );
}
